import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import { Popup } from '@atlaskit/popup';
import { media, xcss } from '@atlaskit/primitives';
import Pressable from '@atlaskit/primitives/pressable';
import Select from '@atlaskit/select';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { breakpointValues } from '../Breakpoints';
import { charlieTextFontFamily } from '../FontFamily';
import { useResponsiveBreakpoint } from '../hooks';
import { mergeRefs } from '../mergeRefs/mergeRefs';
import { CustomControl } from './SelectComponents/Control';
import { CustomMenu } from './SelectComponents/Menu/CustomMenu';
import { CustomOption } from './SelectComponents/Option';
import { sortOptionsConfig } from './SortOptionsConfig';
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
const buttonStyles = xcss(_objectSpread(_objectSpread({}, charlieTextFontFamily), {}, {
  cursor: 'pointer',
  paddingInlineStart: 'space.150',
  paddingInlineEnd: 'space.050',
  paddingBlock: 'space.050',
  borderRadius: 'border.radius',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'color.text',
  fontSize: '0.875rem',
  fontWeight: '400',
  background: 'none',
  ':hover': {
    backgroundColor: 'color.background.neutral.hovered'
  },
  ':active': {
    backgroundColor: 'color.background.neutral.pressed'
  }
}));
const buttonMobileStyles = xcss({
  width: '100%',
  backgroundColor: 'color.background.neutral',
  height: '40px'
});
const customComponents = {
  Option: CustomOption,
  Menu: CustomMenu,
  Control: CustomControl
};
const subtleSelectedStyles = xcss({
  color: 'color.text.selected',
  ':hover': {
    backgroundColor: 'color.background.selected.hovered'
  },
  ':active': {
    backgroundColor: 'color.background.selected.pressed'
  }
});
const customSelectStyles = {
  container: base => _objectSpread(_objectSpread({}, base), {}, {
    width: '180px',
    [media.below.sm]: {
      width: "calc(".concat(window.innerWidth, "px - (", "var(--ds-space-400)", " * 2))")
    },
    [media.below.xs]: {
      width: "calc(".concat(window.innerWidth, "px - (", "var(--ds-space-300)", " * 2))")
    }
  }),
  control: base => _objectSpread(_objectSpread({}, base), {}, {
    border: 0 // Removing the default border around the select control
  }),
  option: base => _objectSpread(_objectSpread({}, base), {}, {
    minHeight: '40px',
    padding: "var(--ds-space-100)".concat(" ", "var(--ds-space-200)")
  })
};
export const SortDropdown = _ref => {
  let {
    onSortOptionSelect,
    selectedSortKey,
    sortOptions = sortOptionsConfig
  } = _ref;
  const {
    value: breakpoint
  } = useResponsiveBreakpoint();
  const isMobile = breakpoint < breakpointValues.sm;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState();
  const triggerRef = useRef(null);
  useEffect(() => {
    setSelectedOption(sortOptions.find(option => option.value === selectedSortKey) || sortOptions[0]);
  }, [selectedSortKey]);
  const onSelectionChange = option => {
    setIsPopupOpen(false);
    setSelectedOption(option);
    if (onSortOptionSelect) onSortOptionSelect(option);
  };
  const focusOnTriggerButton = useCallback(() => {
    setTimeout(() => {
      var _triggerRef$current;
      return (_triggerRef$current = triggerRef.current) === null || _triggerRef$current === void 0 ? void 0 : _triggerRef$current.focus();
    });
  }, []);
  const handlePopupClose = event => {
    if (event.key === 'Escape') {
      focusOnTriggerButton();
    }
    setIsPopupOpen(false);
  };
  const handleSelectKeyDown = event => {
    if (event.key === 'Enter') {
      focusOnTriggerButton();
    }
  };
  return /*#__PURE__*/_jsx(Popup, {
    isOpen: isPopupOpen,
    onClose: handlePopupClose,
    placement: "bottom-start",
    autoFocus: false,
    trigger: triggerProps =>
    /*#__PURE__*/
    _jsxs(Pressable, _objectSpread(_objectSpread({}, triggerProps), {}, {
      ref: mergeRefs(triggerRef, triggerProps.ref),
      onClick: () => setIsPopupOpen(prevState => !prevState),
      xcss: [buttonStyles, isMobile && buttonMobileStyles, isPopupOpen && subtleSelectedStyles],
      children: [/*#__PURE__*/_jsxs("span", {
        children: ["Sort by: ", selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label]
      }), isPopupOpen ? /*#__PURE__*/_jsx(ChevronUpIcon, {
        label: ""
      }) : /*#__PURE__*/_jsx(ChevronDownIcon, {
        label: ""
      })]
    })),
    content: () => /*#__PURE__*/_jsx(Select, {
      testId: "sort-dropdown.select",
      options: sortOptions,
      value: selectedOption,
      onChange: onSelectionChange,
      components: customComponents,
      styles: customSelectStyles,
      menuIsOpen: true,
      autoFocus: true,
      blurInputOnSelect: true,
      closeMenuOnSelect: false,
      hideSelectedOptions: false,
      controlShouldRenderValue: false,
      menuShouldScrollIntoView: false,
      tabSelectsValue: false,
      isClearable: false,
      escapeClearsValue: false,
      isSearchable: false,
      onKeyDown: handleSelectKeyDown
    })
  });
};